import { type ReactElement } from 'react';
import {
  buildTableV2PaginationTokens,
  tableV2FooterControlHeightPx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import { Box, IconButton, MenuItem, Select, Text } from '@/ui/primitives';
import { ChevronRightIcon, KeyboardArrowDownIcon } from '@/ui/primitives/icons';
import { tableControlHoverBackground } from './tablePanelSurfaceSx';
import {
  tableV2FooterCompactPageSizeSelectMinWidth,
  tableV2FooterControlsSx,
  tableV2FooterDefaultPageSizeSelectMinWidth,
  tableV2FooterLabelSx,
  tableV2FooterPageTokensSx,
  tableV2FooterRootSx,
  tableV2FooterSelectRadius,
} from './TableV2Footer.styles';
import type { SxProps, Theme } from '@/ui/primitives';

const paginationDropdownFontSize = '13px';
const paginationChevronIconSize = '22px';
const paginationLabelFontSizePx = 13;
const tableV2FooterLightSelectSurfaceBackground = 'var(--surface-card)';
const tableV2FooterDarkSelectSurfaceBackground = 'var(--surface-inset)';

export const TableV2Footer = ({
  label,
  page,
  pageCount,
  pageSize,
  pageSizeOptions,
  contentInsetX = s('lg'),
  controlHeight = tableV2FooterControlHeightPx,
  labelFontSizePx,
  showControls = true,
  onPageChange,
  onPageSizeChange,
}: {
  label: string;
  page: number;
  pageCount: number;
  pageSize: number;
  pageSizeOptions: number[];
  contentInsetX?: string;
  controlHeight?: number;
  labelFontSizePx?: number;
  showControls?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}): ReactElement => {
  const paginationTokens = buildTableV2PaginationTokens(page, pageCount);
  const previousPageDisabled = page <= 0;
  const nextPageDisabled = page >= pageCount - 1;
  const controlHeightCss = `${String(controlHeight)}px`;
  const paginationChevronButtonSx: SxProps<Theme> = {
    height: `${String(tableV2FooterControlHeightPx)}px`,
    width: `${String(tableV2FooterControlHeightPx)}px`,
    '& > span': {
      height: paginationChevronIconSize,
      width: paginationChevronIconSize,
    },
    '& .MuiSvgIcon-root': {
      fontSize: paginationChevronIconSize,
      height: paginationChevronIconSize,
      width: paginationChevronIconSize,
    },
  };
  const paginationTokenSlotSx: SxProps<Theme> = {
    alignItems: 'center',
    display: 'inline-flex',
    flex: '0 0 auto',
    height: `${String(tableV2FooterControlHeightPx)}px`,
    justifyContent: 'center',
    width: `${String(tableV2FooterControlHeightPx)}px`,
  };

  return (
    <Box sx={tableV2FooterRootSx(contentInsetX)}>
      <Text
        role="body"
        color="textSecondary"
        sx={tableV2FooterLabelSx(labelFontSizePx ?? paginationLabelFontSizePx)}
      >
        {label}
      </Text>
      {showControls ? (
        <Box sx={tableV2FooterControlsSx(contentInsetX)}>
          <Select<number>
            className="flowqa-table-v2-page-size-select"
            fullWidth={false}
            IconComponent={KeyboardArrowDownIcon}
            inputProps={{ 'aria-label': 'Rows per page' }}
            SelectDisplayProps={{
              'aria-label': 'Rows per page',
              style: {
                alignItems: 'center',
                display: 'flex',
                height: controlHeightCss,
                maxHeight: controlHeightCss,
                minHeight: controlHeightCss,
                paddingBottom: 0,
                paddingTop: 0,
              },
            }}
            inputHeight={controlHeight}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    '& .MuiMenuItem-root, & .MuiMenuItem-root .MuiTypography-root, & .MuiMenuItem-root .MuiListItemText-primary':
                      {
                        fontSize: paginationDropdownFontSize,
                      },
                  },
                },
              },
            }}
            value={pageSize}
            onChange={(event) => {
              onPageSizeChange(event.target.value);
            }}
            sx={{
              height: controlHeightCss,
              maxHeight: controlHeightCss,
              minHeight: controlHeightCss,
              minWidth: {
                xs: tableV2FooterCompactPageSizeSelectMinWidth,
                sm: tableV2FooterDefaultPageSizeSelectMinWidth,
              },
              '&.MuiInputBase-root, & .MuiInputBase-root': {
                backgroundColor: (theme) =>
                  isLightTheme(theme)
                    ? tableV2FooterLightSelectSurfaceBackground
                    : tableV2FooterDarkSelectSurfaceBackground,
                height: `${controlHeightCss} !important`,
                maxHeight: `${controlHeightCss} !important`,
                minHeight: `${controlHeightCss} !important`,
              },
              '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
                backgroundColor: (theme) =>
                  isLightTheme(theme)
                    ? tableV2FooterLightSelectSurfaceBackground
                    : tableV2FooterDarkSelectSurfaceBackground,
                borderRadius: tableV2FooterSelectRadius,
                height: `${controlHeightCss} !important`,
                maxHeight: `${controlHeightCss} !important`,
                minHeight: `${controlHeightCss} !important`,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.app.colors.border,
                borderRadius: tableV2FooterSelectRadius,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.app.colors.primary,
                borderWidth: '2px',
              },
              '& .MuiSelect-select, & .MuiSelect-select.MuiInputBase-input': {
                alignItems: 'center',
                boxSizing: 'border-box',
                display: 'flex',
                fontSize: paginationDropdownFontSize,
                height: `${controlHeightCss} !important`,
                lineHeight: 1.4,
                maxHeight: `${controlHeightCss} !important`,
                minHeight: `${controlHeightCss} !important`,
                pl: `${s('lg')} !important`,
                py: `${s('none')} !important`,
                pr: `${s('3xl')} !important`,
              },
              '& .MuiSvgIcon-root': {
                color: 'text.primary',
                fontSize: '24px',
                right: s('md'),
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {String(option)} per page
              </MenuItem>
            ))}
          </Select>
          <IconButton
            aria-label="Go to previous page"
            disabled={previousPageDisabled}
            size="large"
            sx={paginationChevronButtonSx}
            onClick={() => {
              onPageChange(page - 1);
            }}
          >
            <ChevronRightIcon sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
          <Box sx={tableV2FooterPageTokensSx}>
            {paginationTokens.map((token, index) =>
              token === 'ellipsis' ? (
                <Text
                  key={`table-v2-pagination-ellipsis-${String(index)}`}
                  role="body"
                  component="span"
                  color="textSecondary"
                  sx={paginationTokenSlotSx}
                >
                  ...
                </Text>
              ) : (
                <Box
                  key={`table-v2-pagination-page-${String(token)}`}
                  aria-current={token === page ? 'page' : undefined}
                  aria-label={`Go to page ${String(token + 1)}`}
                  component="button"
                  type="button"
                  onClick={() => {
                    onPageChange(token);
                  }}
                  sx={[
                    paginationTokenSlotSx,
                    {
                      backgroundColor: 'transparent',
                      border: '1px solid transparent',
                      borderRadius: r('pill'),
                      color: 'text.primary',
                      cursor: token === page ? 'default' : 'pointer',
                      font: 'inherit',
                      height: s('2xl'),
                      p: s('none'),
                      position: 'relative',
                      width: s('2xl'),
                      '&::after':
                        token === page
                          ? {
                              border: (theme: Theme) => `1px solid ${theme.app.colors.textMuted}`,
                              borderRadius: r('pill'),
                              content: '""',
                              inset: 0,
                              opacity: 0.9,
                              pointerEvents: 'none',
                              position: 'absolute',
                            }
                          : undefined,
                      '&:hover': {
                        backgroundColor:
                          token === page ? 'transparent' : tableControlHoverBackground,
                      },
                    },
                  ]}
                >
                  <Text role="body" component="span" color="inherit">
                    {token + 1}
                  </Text>
                </Box>
              ),
            )}
          </Box>
          <IconButton
            aria-label="Go to next page"
            disabled={nextPageDisabled}
            size="large"
            sx={paginationChevronButtonSx}
            onClick={() => {
              onPageChange(page + 1);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      ) : null}
    </Box>
  );
};
